/* eslint-disable @typescript-eslint/no-explicit-any */
import { addNewHistogramPlotThunk, addNewPlotThunk, getPlotThunk, T_addGridDataView, T_addNewProjectView, T_changeBenchmark, T_getBenchmarkData, T_getBenchmarkDatasets, T_getBenchmarkIndentifierFields, T_getBenchmarkOptions, T_getBenchmarkTypes, T_getHistogramPlotData, T_getProductionPlotData, T_getBenchmarkFieldsFromDatabase, T_addBenchmarkFromFilterSet } from './thunk';
import { ExploreContentTabsInterface, IBenchmarkType, ICategory, IFilteredData, IPlotType, IRegressionFormulas, IUserDataLegend } from './types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import benchmarkingPlot from '../../assets/icons/benchmarkingPlot.svg';
import searchListDark from '../../assets/icons/searchListDark.svg';
import crossplot from '../../assets/icons/crossplot.svg';
import histogram from '../../assets/icons/histogram.svg';
import productionPlot from '../../assets/icons/productionPlot.svg';
import dataGrid from '../../assets/icons/dataGrid.svg';
import statisticsTable from '../../assets/icons/statisticsTable.svg';
import { v4 } from 'uuid';
import { getBenchmarkUserDataColors } from 'helpers/functions/colors';

interface ExploreState {
  selectedTab: ExploreContentTabsInterface,
  tabs: ExploreContentTabsInterface[],
  regressionFormulas: IRegressionFormulas[];
  categories: ICategory[],
  plot: Plotly.Data[],
  imputedIsCategorised: boolean,
  userDataLegends: IUserDataLegend[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  histogram: any,
  productionPlot: {
    plotAxisTitleOptions: any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    productionPlots: any[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    regressionFormulasInfo: any[],
    productionInfoColumns: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      productionInfoColumns: any[],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      productionInfoColumnsRows: any[],
    }
    hiddenByFilterIdentifierFieldIds: string[],

  },
  filteredData: IFilteredData,
  // Whether imputed data is fetched and drawn at all - the Smart Data lightbulb. Not persisted with
  // the plot: imputation is still behind a flag, so this resets each session.
  showImputedData: boolean,
  showImputedLegend: boolean,
  // Whether the legend panel names the user's own data on a histogram. Session state rather than a
  // saved plot option, which the Histogram entity has no column for - unlike the CrossPlot, whose
  // own Show User Data is persisted.
  showUserDataLegend: boolean,
  isLoading: boolean,
  isCrossPlotLoading: boolean,
  isHistogramLoading: boolean,
  isProductionLoading: boolean,
  isPanelBarExpanded: boolean,
  error: null | string,
  addPlotPopup: boolean,
  plotTypesData: IPlotType[],
  benchmark: {
    isFetched: boolean,
    loading: boolean,
    benchmarkTypes: IBenchmarkType[],
    isFetchedBenchmarkTypes: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    benchmark: {
      generatedPlot: any,
      outputTable: {
        columns: any[]
        rows: any
      },
      inputTable: {
        columns: any[]
        rows: any
      }
    }
    indentifierFields: any[],
    datasets: any[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    benchmarkOptions: any
  }
}

const initialState: ExploreState = {
  selectedTab: {
    id: '',
    defaultOption: false,
    icon: '',
    title: '',
    isSelected: false,
    xAxis: 'X',
    yAxis: 'Y',
  },
  filteredData: {
    globalDataCount: 0,
    productionDataCount: 0,
    userDataCount: 0,
    storageDataCount: 0,
    ccsProjectDataCount: 0,
    sourceDataCount: 0,
    transportDataCount: 0,
  },
  isPanelBarExpanded: false,
  showImputedData: false,
  // On, unlike the lightbulb: a series that is being drawn should be named in the legend by default,
  // the way Show Filters and Show User Data are. Unchecking it hides the entry, not the data.
  showImputedLegend: true,
  showUserDataLegend: true,
  userDataLegends: [],
  regressionFormulas: [],
  categories: [],
  imputedIsCategorised: false,
  plot: [
    {
      x: [],
      y: [],
      type: 'scattergl',
      mode: 'markers',
      marker: { color: 'red' },
      hoverinfo: 'none'
    }
  ],
  histogram: {
    data: [],
    statisticsData: [],
    coreStatisticsData: null,
    userStatisticsData: null,
    percentages: [],
    corePercentages: null,
    userPercentages: null,
    imputedHistogram: null,
    userHistogram: null,
    imputedUserHistogram: null,
  },
  productionPlot: {
    plotAxisTitleOptions: {Title: {}, XAxis: {}, YAxis:{}, Y2Axis: {}},
    productionPlots: [],
    regressionFormulasInfo: [],
    productionInfoColumns: {
      productionInfoColumns: [],
      productionInfoColumnsRows: [],
    },
    hiddenByFilterIdentifierFieldIds: [],
  },
  benchmark: {
    loading: false,
    isFetched: false,
    benchmarkTypes: [],
    isFetchedBenchmarkTypes: false,
    benchmarkOptions: {},
    indentifierFields: [],
    datasets: [],
    benchmark: {
      generatedPlot: {},
      outputTable: {
        columns: [],
        rows: []
      },
      inputTable: {
        columns: [],
        rows: []
      },
    }
  },
  tabs: [],
  isLoading: false,
  isCrossPlotLoading: false,
  isHistogramLoading: false,
  isProductionLoading: false,
  error: null,
  addPlotPopup: false,
  plotTypesData: [
    {
      id: v4(),
      image: crossplot,
      text: 'Crossplot',
      active: true,
      disabled: false,
      type: 1,
      setPlotType: () => undefined,
      noData: false,
    },
    {
      id: v4(),
      image: histogram,
      text: 'Histogram',
      active: false,
      disabled: false,
      type: 2,
      setPlotType: () => undefined,
      noData: false,
    },
    // {
    //   id: v4(),
    //   image: statisticsTable,
    //   text: 'Statistics Table',
    //   active: false,
    //   disabled: false,
    //   type: 3,
    //   setPlotType: () => undefined,
    //   noData: true,
    // },
    {
      id: v4(),
      image: dataGrid,
      text: 'Data Grid',
      active: false,
      disabled: false,
      type: 4,
      setPlotType: () => undefined,
      noData: false,
    },
    {
      id: v4(),
      image: productionPlot,
      text: 'Production Plot',
      active: false,
      disabled: false,
      type: 6,
      setPlotType: () => undefined,
      noData: false,
    },
    {
      id: v4(),
      image: benchmarkingPlot,
      text: 'Benchmarking Plot',
      active: false,
      disabled: false,
      type: 7,
      setPlotType: () => undefined,
      noData: false,
    },
    {
      id: v4(),
      image: searchListDark,
      text: 'Project viewer',
      active: false,
      disabled: false,
      type: 5,
      setPlotType: () => undefined,
      noData: false,
    },
  ],
};

export const exploreSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setTabs: (state, action) => {
      state.tabs = action.payload
    },

    setShowImputedData: (state, action: PayloadAction<boolean>) => {
      state.showImputedData = action.payload;
    },

    setShowUserDataLegend: (state, action: PayloadAction<boolean>) => {
      state.showUserDataLegend = action.payload;
    },

    setShowImputedLegend: (state, action: PayloadAction<boolean>) => {
      state.showImputedLegend = action.payload;
    },

    setIsPanelBarExpanded: (state, action) => {
      state.isPanelBarExpanded = action.payload;
    },

    toggleAddPlotPopup: (state) => {
      state.addPlotPopup = !state.addPlotPopup;
    },

    setActivePlotType: (state, action) => {
      state.plotTypesData = state.plotTypesData.map((type: IPlotType) => ({
        ...type,
        active: type.id === action.payload
      }));
    },
    setActivePlotTypeByType: (state, action) => {
      state.plotTypesData = state.plotTypesData.map((type: IPlotType) => ({
        ...type,
        active: type.type === action.payload
      }));
    },

    // Shared source of truth for benchmark row selection. The input and output tables
    // render the same underlying inputs, so a toggle must update both tables' rows in one
    // place — otherwise their independent local copies drift and, because the API is a
    // relative toggle, a second click double-flips the real selection.
    setBenchmarkInputSelected: (state, action) => {
      const { id, value } = action.payload;
      const apply = (rows: any) => {
        if (!Array.isArray(rows)) return;
        rows.forEach((row: any) => {
          if (row?.['[BenchmarkPlotInputId]']?.value === id && row?.['[Selected]']) {
            row['[Selected]'].value = value;
          }
        });
      };
      apply(state.benchmark?.benchmark?.inputTable?.rows);
      apply(state.benchmark?.benchmark?.outputTable?.rows);
    },

    setBenchmarkAllSelected: (state, action) => {
      const { value } = action.payload;
      const apply = (rows: any) => {
        if (!Array.isArray(rows)) return;
        rows.forEach((row: any) => {
          if (row?.['[Selected]']) row['[Selected]'].value = value;
        });
      };
      apply(state.benchmark?.benchmark?.inputTable?.rows);
      apply(state.benchmark?.benchmark?.outputTable?.rows);
    },
  },
  extraReducers: {
    [getPlotThunk.pending.type]: (state) => {
      state.isLoading = true;
      state.isCrossPlotLoading = true;
      
    },

    [getPlotThunk.fulfilled.type]: (state, action) => {
      state.userDataLegends = action.payload.userDataLegends;
      state.plot = action.payload.crossPlots;
      state.regressionFormulas = action.payload.regressionsFormulasInfo;
      state.categories = action.payload.categories
      state.imputedIsCategorised = !!action.payload.imputedIsCategorised
      state.isLoading = false;
      state.isCrossPlotLoading = false
      state.error = null;
    },

    [getPlotThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isCrossPlotLoading = false;
      state.error = action.payload;
    },

    [addNewPlotThunk.pending.type]: (state) => {
      state.isLoading = true;
    },

    [addNewPlotThunk.fulfilled.type]: (state) => {
      state.isLoading = false;
      state.error = null;
    },

    [addNewPlotThunk.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    [T_addNewProjectView.pending.type]: (state) => {
      state.isLoading = true;
    },

    [T_addNewProjectView.fulfilled.type]: (state) => {
      state.isLoading = false;
      state.error = null;
    },

    [T_addNewProjectView.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },


    [T_addGridDataView.pending.type]: (state) => {
      state.isLoading = true;
    },

    [T_addGridDataView.fulfilled.type]: (state) => {
      state.isLoading = false;
      state.error = null;
    },

    [T_addGridDataView.rejected.type]: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    [addNewHistogramPlotThunk.pending.type]: (state) => {
      state.isLoading = true
    },
    [addNewHistogramPlotThunk.fulfilled.type]: (state) => {
      state.isLoading = false
    },
    [addNewHistogramPlotThunk.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
    },

    [T_getHistogramPlotData.pending.type]: (state) => {
      state.isHistogramLoading = true
    },
    [T_getHistogramPlotData.fulfilled.type]: (state, action) => {
      state.histogram = {
        data: action.payload.histogram,
        statisticsData: action.payload.statisticsData,
        percentages: action.payload.percentages,
        // The same two figures over one population each, null when that population is empty.
        coreStatisticsData: action.payload.coreStatisticsData ?? null,
        userStatisticsData: action.payload.userStatisticsData ?? null,
        corePercentages: action.payload.corePercentages ?? null,
        userPercentages: action.payload.userPercentages ?? null,
        // Null unless the API imputed something, which is what the bar trace and the legend key off.
        imputedHistogram: action.payload.imputedHistogram ?? null,
        // Likewise null unless the workspace holds user data. The `data` bucket above counts bMark
        // fields only, so these two are the rest of the stack rather than an extra on top of it.
        userHistogram: action.payload.userHistogram ?? null,
        imputedUserHistogram: action.payload.imputedUserHistogram ?? null
      };
      state.isHistogramLoading = false;
      state.error = null;
    },
    [T_getHistogramPlotData.rejected.type]: (state, action) => {
      state.isHistogramLoading = false
      state.error = action.payload
    },
    [T_getProductionPlotData.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_getProductionPlotData.fulfilled.type]: (state, action) => {
      state.productionPlot = action.payload;
      // state.userDataLegends = action.payload.userDataLegends;
      state.regressionFormulas = action.payload.regressionsFormulasInfo;
      state.isLoading = false;
      state.error = null;
    },
    [T_getProductionPlotData.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
    },

    [T_getBenchmarkTypes.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_getBenchmarkTypes.fulfilled.type]: (state, action) => {
      state.benchmark.benchmarkTypes = action.payload;
      state.isLoading = false;
      state.error = null;
      state.benchmark.isFetchedBenchmarkTypes = true
    },
    [T_getBenchmarkTypes.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      state.benchmark.isFetchedBenchmarkTypes = true
    },

    [T_getBenchmarkData.pending.type]: (state) => {
      state.isLoading = true
      state.benchmark.loading = true
      
    },
    [T_getBenchmarkData.fulfilled.type]: (state, action) => {
      state.benchmark.benchmark = action.payload;
      
      // Extract user data legends from userData array
      const benchmarkUserDataLegends: {name: string, color: string, isUserDataset: boolean}[] = [];

      // The plot payload doesn't record where a record came from, but the input table does:
      // rows added from one of the user's own datasets carry a [DatasetId], records pulled from
      // the bMark database don't. Join on the identifier field id to group the legend by source.
      const userDatasetFieldIds = new Set(
        (action.payload.inputTable?.rows || [])
          .filter((row: any) => row?.['[DatasetId]']?.value)
          .map((row: any) => row?.['[IdentifierFieldId]']?.value)
          .filter(Boolean)
      );

      if (action.payload.generatedPlot?.userData) {
        // Mirrors the per-point colours the benchmark plot draws, so swatches match the points
        const userDataColors = getBenchmarkUserDataColors(action.payload.generatedPlot.userData);

        action.payload.generatedPlot.userData.forEach((userDataItem: any, seriesIndex: number) => {
          if (userDataItem.displayTexts && Array.isArray(userDataItem.displayTexts)) {
            userDataItem.displayTexts.forEach((displayText: string, index: number) => {
              if (displayText && displayText.length > 0) {
                // Get color from markers array or oneMarkerForAll or fallback to default
                let color = '#0000FF'; // default blue color

                if (userDataItem.oneMarkerForAll?.color) {
                  color = userDataItem.oneMarkerForAll.color;
                } else if (userDataColors[seriesIndex]?.[index]) {
                  color = userDataColors[seriesIndex][index];
                } else if (userDataItem.markers && userDataItem.markers[index]?.color) {
                  color = userDataItem.markers[index].color;
                } else if (userDataItem.markers && userDataItem.markers[0]?.color) {
                  color = userDataItem.markers[0].color;
                }

                benchmarkUserDataLegends.push({
                  name: displayText,
                  color: color,
                  isUserDataset: userDatasetFieldIds.has(userDataItem.ids?.[index])
                });
              }
            });
          }
        });
      }
      
      // The API returns points in an order that shifts as records are added, removed or
      // deselected, which makes the legend jump around. Sort it: the user's own data first,
      // then bMark database inputs, alphabetically within each group.
      benchmarkUserDataLegends.sort((a, b) =>
        Number(b.isUserDataset) - Number(a.isUserDataset) ||
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
      );

      state.regressionFormulas = action.payload.regressionsFormulasInfo;
      state.userDataLegends = benchmarkUserDataLegends.map(({ name, color }) => ({ name, color }));
      state.isLoading = false;
      state.benchmark.loading = false
      state.error = null;
      state.benchmark.isFetched = true
    },
    [T_getBenchmarkData.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      state.benchmark.loading = false
      // state.isFetchedBenchmark = true
    },

    [T_getBenchmarkOptions.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_getBenchmarkOptions.fulfilled.type]: (state, action) => {
      state.benchmark.benchmarkOptions = action.payload;
      // state.userDataLegends = action.payload.userDataLegends;
      state.isLoading = false;
      state.error = null;
      // state.isFetchedBenchmark = true
    },
    [T_getBenchmarkOptions.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      // state.isFetchedBenchmark = true
    },







    [T_changeBenchmark.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_changeBenchmark.fulfilled.type]: (state, action) => {
      state.benchmark.benchmarkOptions = action.payload;


      // state.userDataLegends = action.payload.userDataLegends;
      state.isLoading = false;
      state.error = null;
      // state.isFetchedBenchmark = true
    },
    [T_changeBenchmark.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      // state.isFetchedBenchmark = true
    },


    [T_getBenchmarkDatasets.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_getBenchmarkDatasets.fulfilled.type]: (state, action) => {
      state.benchmark.datasets = action.payload;


      // state.userDataLegends = action.payload.userDataLegends;
      state.isLoading = false;
      state.error = null;
      // state.isFetchedBenchmark = true
    },
    [T_getBenchmarkDatasets.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      // state.isFetchedBenchmark = true
    },




    [T_getBenchmarkIndentifierFields.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_getBenchmarkIndentifierFields.fulfilled.type]: (state, action) => {
      state.benchmark.indentifierFields = action.payload;


      // state.userDataLegends = action.payload.userDataLegends;
      state.isLoading = false;
      state.error = null;
      // state.isFetchedBenchmark = true
    },
    [T_getBenchmarkIndentifierFields.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
      // state.isFetchedBenchmark = true
    },

    // T_getBenchmarkFieldsFromDatabase handlers
    [T_getBenchmarkFieldsFromDatabase.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_getBenchmarkFieldsFromDatabase.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      state.error = null;
    },
    [T_getBenchmarkFieldsFromDatabase.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
    },

    // T_addBenchmarkFromFilterSet handlers
    [T_addBenchmarkFromFilterSet.pending.type]: (state) => {
      state.isLoading = true
    },
    [T_addBenchmarkFromFilterSet.fulfilled.type]: (state, action) => {
      state.isLoading = false;
      state.error = null;
    },
    [T_addBenchmarkFromFilterSet.rejected.type]: (state, action) => {
      state.isLoading = false
      state.error = action.payload
    },

  },
});

export default exploreSlice.reducer;
